import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';
const FORM_URL = 'https://readdy.ai/api/form/d785us1rcamcd36dau10';

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = '연락처 & 문의 | 필그린 인테리어 강남 상담 문의';
    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.content = content;
    };
    setMeta('description', '필그린 인테리어 연락처 및 문의. 서울 강남구 양재대로55길6. 전화 010-5350-7926. 평일 09:00~18:00, 토요일 10:00~15:00.');
    setMeta('og:title', '연락처 & 문의 | 필그린 인테리어 강남 상담 문의', true);
    setMeta('og:url', `${SITE_URL}/contact`, true);

    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: '필그린 인테리어 연락처',
      url: `${SITE_URL}/contact`,
      mainEntity: {
        '@type': 'LocalBusiness',
        name: '필그린 인테리어',
        telephone: '010-5350-7926',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '양재대로55길6 종합상가 203호',
          addressLocality: '강남구',
          addressRegion: '서울특별시',
          postalCode: '06336',
          addressCountry: 'KR',
        },
        openingHoursSpecification: [
          { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '09:00', closes: '18:00' },
          { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '10:00', closes: '15:00' },
        ],
      },
    };

    const scriptId = 'jsonld-contact';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) { script = document.createElement('script'); script.id = scriptId; script.type = 'application/ld+json'; document.head.appendChild(script); }
    script.textContent = JSON.stringify(jsonLd);
    return () => { document.getElementById(scriptId)?.remove(); };
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch(FORM_URL, { method: 'POST', body: data, headers: { 'Content-Type': 'application/x-www-form-urlencoded' } });
      setSubmitted(true);
    } catch {
      alert('제출 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#f7f5f3] min-h-screen">
      <Navbar />
      {/* Hero */}
      <section className="relative h-72 flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://public.readdy.ai/ai/img_res/4503faf8491b3bc6379c770b52c407fb.jpg" alt="연락처" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-black/40" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 pb-12 w-full">
          <span className="text-[#c9a84c] text-xs tracking-[0.3em] uppercase font-medium">Contact</span>
          <h1 className="text-3xl md:text-5xl font-light text-white mt-2 leading-snug md:leading-tight" style={{ fontFamily: "'Noto Serif KR', serif" }}>연락처</h1>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
          {/* Info */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-semibold text-[#1a1a1a] mb-5 md:mb-6 leading-snug" style={{ fontFamily: "'Noto Serif KR', serif" }}>기업 정보</h2>
              <div className="space-y-5">
                {[
                  { icon: 'ri-store-line', label: '기업상호', value: '필그린' },
                  { icon: 'ri-user-line', label: '대표자', value: '장인규' },
                  { icon: 'ri-file-list-line', label: '사업자등록번호', value: '213-10-24418' },
                  { icon: 'ri-smartphone-line', label: '연락처', value: '010-5350-7926' },
                  { icon: 'ri-map-pin-line', label: '주소', value: '서울특별시 강남구 양재대로55길6\n(종합상가 203호)' },
                  { icon: 'ri-bank-line', label: '계좌번호', value: '농협 301-0231-3153-81\n(예금주: 장인규)' },
                  { icon: 'ri-time-line', label: '운영시간', value: '평일 09:00 – 18:00\n토요일 10:00 – 15:00 (일요일 휴무)' },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#c9a84c]/10 shrink-0">
                      <i className={`${item.icon} text-[#c9a84c] text-lg`} />
                    </div>
                    <div>
                      <div className="text-[#999] text-xs mb-1">{item.label}</div>
                      <div className="text-[#1a1a1a] text-sm font-medium whitespace-pre-line">{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Map */}
            <div className="w-full h-64 rounded-2xl overflow-hidden border border-[#e8e4dc]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3162.8!2d127.0384!3d37.4851!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x357ca5b0e3b4e5f%3A0x0!2z7ISc7Jq47Yq567OE7IucIOqwleuCqOq1rCDslYTsnbTroZzroZw1NuquiCA2!5e0!3m2!1sko!2skr!4v1700000000001"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                title="필그린 위치"
              />
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <h2 className="text-2xl md:text-3xl font-semibold text-[#1a1a1a] mb-6 md:mb-8 leading-snug" style={{ fontFamily: "'Noto Serif KR', serif" }}>문의하기</h2>
            {submitted ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-[#e8e4dc]">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#c9a84c]/10 mx-auto mb-4">
                  <i className="ri-check-line text-[#c9a84c] text-3xl" />
                </div>
                <h3 className="text-xl font-semibold text-[#1a1a1a] mb-2">문의가 접수되었습니다!</h3>
                <p className="text-[#666] text-sm">빠른 시일 내에 답변드리겠습니다.</p>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 md:p-10 border border-[#e8e4dc] space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-[#333] mb-2">이름 *</label>
                    <input name="name" required className="w-full border border-[#ddd] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#c9a84c]" placeholder="홍길동" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#333] mb-2">연락처 *</label>
                    <input name="phone" required className="w-full border border-[#ddd] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#c9a84c]" placeholder="010-0000-0000" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#333] mb-2">이메일 *</label>
                  <input name="email" type="email" required className="w-full border border-[#ddd] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#c9a84c]" placeholder="example@email.com" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#333] mb-2">문의 유형</label>
                  <select name="inquiry_type" className="w-full border border-[#ddd] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#c9a84c] bg-white cursor-pointer">
                    <option value="">선택해주세요</option>
                    {['견적 문의', '시공 상담', 'A/S 문의', '기타 문의'].map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#333] mb-2">제목 *</label>
                  <input name="subject" required className="w-full border border-[#ddd] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#c9a84c]" placeholder="문의 제목을 입력해주세요" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#333] mb-2">내용 *</label>
                  <textarea name="message" required maxLength={500} rows={5} className="w-full border border-[#ddd] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#c9a84c] resize-none" placeholder="문의 내용을 자유롭게 작성해주세요. (최대 500자)" />
                </div>
                <button type="submit" disabled={submitting} className="w-full bg-[#c9a84c] text-[#0d0d0d] font-bold py-4 rounded-full text-sm cursor-pointer hover:bg-[#b8973b] transition-colors whitespace-nowrap disabled:opacity-60">
                  {submitting ? '제출 중...' : '문의 보내기'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
